import axios from 'axios'
import queryString from 'query-string'
import Subscriber from '../utils/Subscriber'
import captcha from '../utils/captcha'

axios.defaults.withCredentials = true

export default class ApiConnector {
  alertSubscriber = new Subscriber()
  barcodeSubscriber = new Subscriber()
  onNavigateTo
  _createCaptchaToken

  msal
  _msalLogout
  _msalTokenCreator

  constructor(baseUrl) {
    this.baseUrl = baseUrl
  }

  setupMsalTokenCreator(fn) {
    this._msalTokenCreator = fn
  }

  setupMsal(msal) {
    this.msal = msal
  }

  setupMsalLogout(fn) {
    this._msalLogout = fn
  }

  setupCaptcha(captchaType, captchaId, turnStileSiteKey, captchaVisibility) {
    this._createCaptchaToken = captcha(captchaType, captchaId, turnStileSiteKey, captchaVisibility)
  }

  listenToAlerts = listener => {
    return this.alertSubscriber.subscribe(listener)
  }

  listenToBarcodes = listener => {
    return this.barcodeSubscriber.subscribe(listener)
  }

  makeRequest(url, method, config = {}, data) {
    const source = axios.CancelToken.source()
    config = { ...config, cancelToken: source.token }

    let request = this._makeAsyncRequest(url, method, config, data)

    return {
      request,
      cancel: () => {
        source.cancel('cancelled')
      },
    }
  }

  async _makeAsyncRequest(url, method, config = {}, data) {
    if (!config.headers) config.headers = {}

    // this is the origin of the issue
    // config.validateStatus = status => true

    if (this._msalTokenCreator) {
      const token = await this._msalTokenCreator()
      if (token) config.headers['Authorization'] = `Bearer ${token}`
    }

    let responsePromise

    if (method === 'post') {
      let token
      if (this._createCaptchaToken) {
        token = await this._createCaptchaToken('default')
      }

      if (config && config.newTab) {
        const queryParams = data ? `?${queryString.stringify(data)}` : ''
        window.open(`${this.baseUrl}${url}${queryParams}`, '_blank')
        return
      }

      const requestData = data || {}
      const requestConfig = config || { headers: {} }

      if (token) {
        if (requestData instanceof FormData) {
          requestData.append('token', token)
        } else {
          requestData.token = token
        }
        requestConfig.headers = {
          ...requestConfig.headers,
          'x-captcha-token': token,
        }
      }

      responsePromise = axios.post(this.baseUrl + url, requestData, requestConfig)
    } else {
      if (data !== undefined) {
        if (config && config.newTab) {
          window.open(`${this.baseUrl}${url}?${queryString.stringify(data)}`, '_blank')
          return
        }
        responsePromise = axios[method](this.baseUrl + url, data, config)
      } else {
        responsePromise = axios[method](this.baseUrl + url, config)
      }
    }

    let reponseData
    try {
      ;({ data: reponseData } = await responsePromise)
    } catch (error) {
      if (axios.isCancel(error)) {
        // since this request is cancelled
        // we don't want it to resolve/reject anymore
        return new Promise(() => {})
      }

      const { response: { data: { navigateTo, alert, logoutClient, reload = false } = {} } = {} } =
        error
      if (alert) this.dispatchAlert(alert)
      setTimeout(() => {
        if (logoutClient) this._msalLogout && this._msalLogout()
      }, 3000)

      if (navigateTo) this.onNavigateTo && this.onNavigateTo(navigateTo, reload)

      throw error
    }

    const { navigateTo, alert, scan, logoutClient, reload = false } = reponseData
    if (logoutClient) this._msalLogout && this._msalLogout()
    if (navigateTo) this.onNavigateTo && this.onNavigateTo(navigateTo, reload)
    if (alert) this.dispatchAlert(alert)

    return reponseData
  }

  dispatchAlert = alert => {
    this.alertSubscriber.dispatch(alert)
  }

  dispatchBarcode = data => {
    this.barcodeSubscriber.dispatch(data)
  }

  get = (url, config, data) => {
    return this.makeRequest(url, 'get', config, data)
  }

  delete = (url, config) => {
    return this.makeRequest(url, 'delete', config)
  }

  post = (url, data, config) => {
    return this.makeRequest(url, 'post', config, data)
  }

  put = (url, data, config) => {
    return this.makeRequest(url, 'put', config, data)
  }

  patch = (url, data, config) => {
    return this.makeRequest(url, 'patch', config, data)
  }
}
